import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { setBartenderTrack, getMixologistTrackStatus } from "@/lib/mixologist.functions";

export const Route = createFileRoute("/_authenticated/bartender/role-select")({
  head: () => ({ meta: [{ title: "Choose Your Expertise — The Hospitality Reserve" }] }),
  component: RoleSelectPage,
});

function RoleSelectPage() {
  const navigate = useNavigate();
  const setTrack = useServerFn(setBartenderTrack);
  const status = useServerFn(getMixologistTrackStatus);
  const [submitting, setSubmitting] = useState<"bartender" | "mixologist" | null>(null);
  const [hydrating, setHydrating] = useState(true);

  useEffect(() => {
    (async () => {
      const r = await status();
      if ("ok" in r && r.profile?.mixologist_track_status) {
        navigate({ to: "/bartender/application", replace: true });
        return;
      }
      setHydrating(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = async (kind: "bartender" | "mixologist") => {
    setSubmitting(kind);
    try {
      const r = await setTrack({ data: { is_mixologist: kind === "mixologist" } });
      if ("error" in r) throw new Error(r.error);
      navigate({ to: "/bartender/application", replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save your choice.");
      setSubmitting(null);
    }
  };

  if (hydrating) return <div className="min-h-dvh bg-background" aria-busy="true" />;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-6 py-5 border-b border-border">
        <p className="tracking-wide-luxury text-accent text-xs">The Hospitality Reserve</p>
        <p className="font-display text-lg leading-tight">Choose Your Expertise Level</p>
      </header>
      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-2xl">
          <p className="tracking-wide-luxury text-accent text-xs text-center">Step 1 of onboarding</p>
          <span className="block mx-auto mt-5 h-px w-12 bg-accent/60" />
          <h1 className="mt-6 font-display text-3xl text-center">Which best describes you?</h1>
          <p className="mt-3 text-sm text-muted-foreground text-center max-w-md mx-auto">
            Mixologists complete the same automated CV check <em>plus</em> an advanced spec assessment and
            CV scrutiny by our team.
          </p>

          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => pick("bartender")}
              disabled={submitting !== null}
              className="text-left border border-border bg-white p-6 hover:border-accent transition-colors disabled:opacity-60"
            >
              <p className="tracking-wide-luxury text-xs text-accent">Standard</p>
              <p className="mt-2 font-display text-2xl">Bartender</p>
              <p className="mt-3 text-sm text-muted-foreground">
                Upload your CV — we verify your experience automatically.
              </p>
              <p className="mt-6 tracking-wide-luxury text-xs text-foreground">
                {submitting === "bartender" ? "Saving…" : "Continue as Bartender →"}
              </p>
            </button>
            <button
              type="button"
              onClick={() => pick("mixologist")}
              disabled={submitting !== null}
              className="text-left border-2 border-accent bg-white p-6 hover:bg-accent/5 transition-colors disabled:opacity-60"
            >
              <p className="tracking-wide-luxury text-xs text-accent">Advanced</p>
              <p className="mt-2 font-display text-2xl">Mixologist</p>
              <p className="mt-3 text-sm text-muted-foreground">
                Automated CV check plus the timed "Fix this spec" assessment and manual CV scrutiny.
                Fail the advanced section and you still onboard as a bartender.
              </p>
              <p className="mt-6 tracking-wide-luxury text-xs text-accent">
                {submitting === "mixologist" ? "Saving…" : "Continue as Mixologist →"}
              </p>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
